import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface FileAttachment {
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
}

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
  attachments?: FileAttachment[];
}

interface UseAIStreamingOptions {
  conversationId?: string;
  onMessageSaved?: (role: 'user' | 'assistant', content: string) => void;
}

interface UseAIStreamingReturn {
  messages: Message[];
  isStreaming: boolean;
  isUploading: boolean;
  sendMessage: (content: string, model: string, enableTools?: boolean, files?: File[]) => Promise<void>;
  clearMessages: () => void;
  setMessages: (messages: Message[]) => void;
}

export const useAIStreaming = (options?: UseAIStreamingOptions): UseAIStreamingReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const uploadFilesToStorage = async (files: File[]): Promise<FileAttachment[]> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('User not authenticated');

    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${session.user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('ai-chat-attachments')
        .upload(fileName, file, {
          contentType: file.type,
          cacheControl: '3600',
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('ai-chat-attachments')
        .getPublicUrl(data.path);

      return {
        fileName: file.name,
        fileUrl: publicUrl,
        fileType: file.type,
        fileSize: file.size,
      };
    });

    return Promise.all(uploadPromises);
  };

  const saveMessageToDb = async (role: 'user' | 'assistant', content: string, attachments?: FileAttachment[]) => {
    if (options?.conversationId) {
      try {
        await supabase
          .from('ai_conversation_messages')
          .insert([{
            conversation_id: options.conversationId,
            role,
            content,
            attachments: (attachments || []) as any,
          }]);

        // Update conversation's updated_at
        await supabase
          .from('ai_conversations')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', options.conversationId);

        options.onMessageSaved?.(role, content);
      } catch (error) {
        console.error('Error saving message to DB:', error);
      }
    }
  };

  const sendMessage = useCallback(async (content: string, model: string, enableTools = true, files?: File[]) => {
    let uploadedFiles: FileAttachment[] = [];

    try {
      // Upload files if provided
      if (files && files.length > 0) {
        setIsUploading(true);
        uploadedFiles = await uploadFilesToStorage(files);
        setIsUploading(false);
      }

      // Add user message
      const userMessage: Message = {
        role: 'user',
        content,
        timestamp: new Date(),
        attachments: uploadedFiles,
      };
      setMessages(prev => [...prev, userMessage]);
      setIsStreaming(true);

      // Save user message to DB
      await saveMessageToDb('user', content, uploadedFiles);

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-assistant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map(({ role, content }) => ({ role, content })),
            model,
            enableTools,
            attachments: uploadedFiles,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      // Add initial assistant message
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: '', timestamp: new Date() },
      ]);

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const delta = parsed.choices?.[0]?.delta?.content;

              if (delta) {
                assistantMessage += delta;
                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = {
                    role: 'assistant',
                    content: assistantMessage,
                    timestamp: new Date(),
                  };
                  return newMessages;
                });
              }
            } catch (e) {
              // Skip invalid JSON
              console.warn('Failed to parse SSE data:', e);
            }
          }
        }
      }

      // Save complete assistant message to DB
      await saveMessageToDb('assistant', assistantMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsUploading(false);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `Erro: ${error instanceof Error ? error.message : 'Falha ao obter resposta'}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [messages, options]);

  return {
    messages,
    isStreaming,
    isUploading,
    sendMessage,
    clearMessages,
    setMessages,
  };
};
